import React, { useEffect, useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, X } from 'lucide-react';
export function TextVersionToggle() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);
  return <>
      <motion.button onClick={() => setOpen(true)} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1
    }} whileHover={{
      scale: 1.05
    }} whileTap={{
      scale: 0.95
    }} className="fixed top-6 right-6 z-40 px-4 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white text-sm font-medium rounded-full flex items-center gap-2 transition-colors" aria-label="Read text version of the concept" data-id="element-86">
        <FileText className="w-4 h-4" data-id="element-87" />
        Read text version
      </motion.button>

      <AnimatePresence data-id="element-88">
        {open && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 md:p-12" onClick={() => setOpen(false)} data-id="element-89">
            <motion.div initial={{
          opacity: 0,
          y: 30,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 30,
          scale: 0.98
        }} transition={{
          duration: 0.3
        }} onClick={e => e.stopPropagation()} className="relative bg-white rounded-2xl max-w-3xl w-full my-auto p-8 md:p-12 shadow-2xl" data-id="element-90">
              <button onClick={() => setOpen(false)} className="absolute top-6 right-6 w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors" aria-label="Close" data-id="element-91">
                <X className="w-5 h-5 text-slate-700" data-id="element-92" />
              </button>

              <div className="mb-8" data-id="element-93">
                <div className="text-xs font-semibold text-red-600 tracking-wider mb-3" data-id="element-94">
                  CONCEPT SUMMARY
                </div>
                <h2 className="font-editorial text-3xl md:text-4xl font-bold text-slate-900 mb-3" data-id="element-95">
                  The Tralo Master-Class
                </h2>
                <p className="text-slate-600" data-id="element-96">
                  A speaker-deck concept for Leo Pharma — 6 KEE videos, 4
                  experiential layers
                </p>
              </div>

              <div className="space-y-8 text-slate-700 leading-relaxed" data-id="element-97">
                <section data-id="element-98">
                  <h3 className="font-semibold text-slate-900 text-lg mb-2" data-id="element-99">
                    The brief
                  </h3>
                  <p data-id="element-100">
                    Leo Pharma needs a series of 6 KEE videos (3–4 minutes each)
                    that teach other HCPs how to present favorable Tralokinumab
                    trial data. The concept needs to achieve three things at
                    once: enable HCP audiences to actually present the data
                    confidently, leverage strong KEEs as indirect advocates for
                    Tralo, and reward the speakers' professional vanity by
                    elevating them as recognized masters.
                  </p>
                </section>

                <section data-id="element-101">
                  <h3 className="font-semibold text-slate-900 text-lg mb-2" data-id="element-102">
                    The four layers
                  </h3>
                  <ol className="space-y-4 list-decimal list-outside ml-5" data-id="element-103">
                    <li data-id="element-104">
                      <span className="font-semibold text-slate-900" data-id="element-105">
                        Master-class Intro.
                      </span>{' '}
                      The video opens like a Netflix master-class teaser. Each
                      of the 6 KEEs is introduced with a cinematic headshot,
                      name, country, and specialty. The HCP audience thinks "oh,
                      that's Dr. so-and-so from Argentina — she's smart." Leo
                      borrows their credibility. The speakers feel famous.
                    </li>
                    <li data-id="element-106">
                      <span className="font-semibold text-slate-900" data-id="element-107">
                        The Menu.
                      </span>{' '}
                      Viewers land on a streaming-style menu where they choose
                      by speaker or by topic (Efficacy, Safety, Long-term data,
                      Quality of Life, Real-world evidence, Patient Selection).
                      They control their own learning path.
                    </li>
                    <li data-id="element-108">
                      <span className="font-semibold text-slate-900" data-id="element-109">
                        Scientific Core.
                      </span>{' '}
                      The 6 chapters themselves are intentionally clean and
                      vanilla — KEEs walk through study graphs with practical
                      presenting tips. Keeping this layer unbiased and editorial
                      is what makes the rest of the experience credible.
                    </li>
                    <li data-id="element-110">
                      <span className="font-semibold text-slate-900" data-id="element-111">
                        X-Ray Overlay.
                      </span>{' '}
                      A Prime Video-style optional toggle. Activate it and a
                      semi-transparent panel reveals the speaker's CV and
                      merits, the specific study being referenced (ECZTRA
                      1/2/3), and download options for slides, citations, and
                      bios. It adds depth without disrupting the core.
                    </li>
                  </ol>
                </section>

                <section data-id="element-112">
                  <h3 className="font-semibold text-slate-900 text-lg mb-2" data-id="element-113">
                    Why it works
                  </h3>
                  <p data-id="element-114">
                    Each layer pulls a different lever. Layer 1 hits speaker
                    vanity and KEE advocacy instantly. Layer 2 makes HCPs feel
                    in control. Layer 3 protects scientific credibility. Layer 4
                    rewards the curious viewer with rich context and take-aways
                    — and gives Leo a memorable differentiator.
                  </p>
                </section>

                <section className="pt-4 border-t border-slate-200" data-id="element-115">
                  <h3 className="font-semibold text-slate-900 text-lg mb-2" data-id="element-116">
                    Next steps
                  </h3>
                  <p data-id="element-117">
                    Refine speaker selection, finalize the topic structure, and
                    begin production planning for the 6-video series.
                  </p>
                </section>
              </div>

              <div className="mt-10 pt-6 border-t border-slate-200 flex items-center justify-between text-sm text-slate-500" data-id="element-118">
                <span data-id="element-119">Prepared for Leo Pharma</span>
                <button onClick={() => setOpen(false)} className="text-red-600 font-medium hover:text-red-700 transition-colors" data-id="element-120">
                  Back to visual storyboard →
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}